import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { paymentsService, refundsService } from '../services/api';
import { CreditCard as CreditCardIcon, Hourglass as HourglassIcon, Trash2 as Trash2Icon } from 'lucide-react';

const PaymentHistory = ({ booking, onRefund, user }) => {
  const [payments, setPayments] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Payment management states
  const [editingPayment, setEditingPayment] = useState(null);
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [deletingPayment, setDeletingPayment] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Refund management states
  const [editingRefund, setEditingRefund] = useState(null);
  const [updatingRefund, setUpdatingRefund] = useState(false);
  const [deletingRefund, setDeletingRefund] = useState(null);
  const [showDeleteRefundConfirm, setShowDeleteRefundConfirm] = useState(false);
  
  // Form states for editing
  const [editForm, setEditForm] = useState({
    amount: '',
    status: '',
    method: '',
    transactionId: '',
    paymentDate: '',
    reason: '',
    adminNote: ''
  });
  
  // Form states for deletion
  const [deleteForm, setDeleteForm] = useState({
    reason: '',
    adminNote: ''
  });
  
  // Form states for refund editing
  const [editRefundForm, setEditRefundForm] = useState({
    amount: '',
    status: '',
    method: '',
    transactionId: '',
    reason: '',
    adminNote: ''
  });
  
  // Form states for refund deletion
  const [deleteRefundForm, setDeleteRefundForm] = useState({
    reason: '',
    adminNote: ''
  });
  
  const isAdmin = user?.role === 'SUPERADMIN' || user?.role === 'PROPERTYADMIN';
  const paymentInvoiceRef = useRef(null);
  const [invoicePayment, setInvoicePayment] = useState(null);

  const loadPayments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await paymentsService.getByBooking(booking.id);
      setPayments(response.data || response || []);
    } catch (error) {
      console.error('Error loading payments:', error);
      toast.error('Failed to load payment history');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [booking.id]);

  const loadRefunds = useCallback(async () => {
    try {
      const response = await refundsService.getByBooking(booking.id);
      console.log('Loaded refunds:', response);
      setRefunds(response.data || response || []);
    } catch (error) {
      console.error('Error loading refunds:', error);
      // Don't show error toast for refunds as it's secondary data
      setRefunds([]);
    }
  }, [booking.id]);

  useEffect(() => {
    loadPayments();
    loadRefunds();
  }, [loadPayments, loadRefunds]);

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS':
        return { bg: '#E1E9E2', text: '#0F2619', border: '#C4D4C8' };
      case 'PENDING':
        return { bg: '#F3E6CB', text: '#684A1F', border: '#E8CF9C' };
      case 'FAILED':
        return { bg: '#E5B3A9', text: '#5F241B', border: '#D48A7C' };
      case 'REFUNDED':
        return { bg: '#E1E9E2', text: '#0F2619', border: '#C4D4C8' };
      default:
        return { bg: '#EEEBE1', text: '#36433B', border: '#D2CDBF' };
    }
  };

  const formatDateIST = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const formatDateOnlyIST = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleDownloadPaymentInvoice = async (payment) => {
    setInvoicePayment(payment);
    // Wait for state + render
    await new Promise(r => setTimeout(r, 100));
    if (!paymentInvoiceRef.current) return;
    try {
      const canvas = await html2canvas(paymentInvoiceRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`payment-invoice-${booking?.bookingNumber || 'payment'}-${payment.id.slice(-8)}.pdf`);
      toast.success('Payment invoice downloaded!');
    } catch (error) {
      console.error('Error generating payment invoice PDF:', error);
      toast.error('Failed to download payment invoice');
    } finally {
      setInvoicePayment(null);
    }
  };

  const getMethodDisplay = (method) => {
    const methodMap = {
      'cash': { label: 'Cash' },
      'stripe': { label: 'Stripe' },
      'netbanking': { label: 'Net Banking' },
      'credit_card': { label: 'Credit Card' },
      'debit_card': { label: 'Debit Card' },
      'upi': { label: 'UPI' },
      'razorpay': { label: 'Razorpay' },
    };
    
    const methodInfo = methodMap[method] || { label: method };
    return `${methodInfo.label}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleRefund = (payment) => {
    if (onRefund) {
      onRefund(payment);
    }
  };

  // Start editing a payment
  const handleEditPayment = (payment) => {
    setEditingPayment(payment.id);
    setEditForm({
      amount: payment.amount.toString(),
      status: payment.status,
      method: payment.method,
      transactionId: payment.transactionId || '',
      paymentDate: payment.paymentDate ? new Date(payment.paymentDate).toISOString().split('T')[0] : '',
      reason: '',
      adminNote: ''
    });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingPayment(null);
    setEditForm({
      amount: '',
      status: '',
      method: '',
      transactionId: '',
      paymentDate: '',
      reason: '',
      adminNote: ''
    });
  };

  // Update payment
  const handleUpdatePayment = async () => {
    if (!editForm.reason.trim()) {
      toast.error('Please provide a reason for updating this payment');
      return;
    }

    if (!editForm.amount || parseFloat(editForm.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    setUpdatingPayment(true);
    try {
      const updateData = {
        amount: parseFloat(editForm.amount),
        status: editForm.status,
        method: editForm.method,
        transactionId: editForm.transactionId || undefined,
        paymentDate: editForm.paymentDate || undefined,
        reason: editForm.reason.trim(),
        adminNote: editForm.adminNote.trim() || undefined
      };

      await paymentsService.updatePayment(editingPayment, updateData);
      toast.success('Payment updated successfully!');
      
      // Refresh payments
      await loadPayments();
      handleCancelEdit();
    } catch (error) {
      console.error('Error updating payment:', error);
      toast.error(error.response?.data?.error || 'Failed to update payment. Please try again.');
    } finally {
      setUpdatingPayment(false);
    }
  };

  // Show delete confirmation
  const handleShowDeleteConfirm = (payment) => {
    setDeletingPayment(payment);
    setDeleteForm({
      reason: '',
      adminNote: ''
    });
    setShowDeleteConfirm(true);
  };

  // Cancel delete
  const handleCancelDelete = () => {
    setDeletingPayment(null);
    setShowDeleteConfirm(false);
    setDeleteForm({
      reason: '',
      adminNote: ''
    });
  };

  // Delete payment
  const handleDeletePayment = async () => {
    if (!deleteForm.reason.trim()) {
      toast.error('Please provide a reason for deleting this payment');
      return;
    }

    if (!window.confirm(`Are you sure you want to permanently delete this payment of ₹${deletingPayment.amount}? This action cannot be undone.`)) {
      return;
    }

    setUpdatingPayment(true);
    try {
      await paymentsService.deletePayment(deletingPayment.id, {
        reason: deleteForm.reason.trim(),
        adminNote: deleteForm.adminNote.trim() || undefined
      });

      toast.success('Payment deleted successfully!');
      
      // Refresh payments
      await loadPayments();
      handleCancelDelete();
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error(error.response?.data?.error || 'Failed to delete payment. Please try again.');
    } finally {
      setUpdatingPayment(false);
    }
  };

  // ====== REFUND HANDLERS ======
  
  // Start editing a refund
  const handleEditRefund = (refund) => {
    setEditingRefund(refund.id);
    setEditRefundForm({
      amount: refund.amount.toString(),
      status: refund.status || 'PENDING',
      method: refund.method || refund.refundMethod || 'cash',
      transactionId: refund.transactionId || '',
      reason: refund.reason || '',
      adminNote: ''
    });
  };

  // Cancel refund editing
  const handleCancelRefundEdit = () => {
    setEditingRefund(null);
    setEditRefundForm({
      amount: '',
      status: '',
      method: '',
      transactionId: '',
      reason: '',
      adminNote: ''
    });
  };

  // Update refund
  const handleUpdateRefund = async () => {
    if (!editRefundForm.reason.trim()) {
      toast.error('Please provide a reason for the refund');
      return;
    }

    if (!editRefundForm.amount || parseFloat(editRefundForm.amount) <= 0) {
      toast.error('Please enter a valid refund amount');
      return;
    }

    setUpdatingRefund(true);
    try {
      const updateData = {
        amount: parseFloat(editRefundForm.amount),
        status: editRefundForm.status,
        method: editRefundForm.method,
        transactionId: editRefundForm.transactionId || undefined,
        reason: editRefundForm.reason.trim(),
        notes: editRefundForm.adminNote.trim() || undefined
      };

      await refundsService.updateRefund(editingRefund, updateData);
      toast.success('Refund updated successfully!');
      
      // Refresh refunds
      await loadRefunds();
      handleCancelRefundEdit();
    } catch (error) {
      console.error('Error updating refund:', error);
      toast.error(error.response?.data?.error || 'Failed to update refund. Please try again.');
    } finally {
      setUpdatingRefund(false);
    }
  };

  // Show delete refund confirmation
  const handleShowDeleteRefundConfirm = (refund) => {
    setDeletingRefund(refund);
    setDeleteRefundForm({
      reason: '',
      adminNote: ''
    });
    setShowDeleteRefundConfirm(true);
  };

  // Cancel delete refund
  const handleCancelDeleteRefund = () => {
    setDeletingRefund(null);
    setShowDeleteRefundConfirm(false);
    setDeleteRefundForm({
      reason: '',
      adminNote: ''
    });
  };

  // Delete refund
  const handleDeleteRefund = async () => {
    if (!deleteRefundForm.reason.trim()) {
      toast.error('Please provide a reason for deleting this refund');
      return;
    }

    if (!window.confirm(`Are you sure you want to permanently delete this refund of ₹${deletingRefund.amount}? This action cannot be undone.`)) {
      return;
    }

    setUpdatingRefund(true);
    try {
      await refundsService.delete(deletingRefund.id, {
        reason: deleteRefundForm.reason.trim(),
        adminNote: deleteRefundForm.adminNote.trim() || undefined
      });

      toast.success('Refund deleted successfully!');
      
      // Refresh refunds
      await loadRefunds();
      handleCancelDeleteRefund();
    } catch (error) {
      console.error('Error deleting refund:', error);
      toast.error(error.response?.data?.error || 'Failed to delete refund. Please try again.');
    } finally {
      setUpdatingRefund(false);
    }
  };

  const totalPaid = payments
    .filter(p => p.status === 'SUCCESS')
    .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

  // Debug: Log individual refund details
  console.log('=== DETAILED REFUND ANALYSIS === (Updated)');
  console.log('Total refunds count:', refunds.length);
  refunds.forEach((refund, index) => {
    console.log(`Refund ${index + 1}:`, {
      id: refund.id,
      amount: refund.amount,
      amountType: typeof refund.amount,
      amountParsed: parseFloat(refund.amount),
      amountIsNaN: isNaN(parseFloat(refund.amount)),
      status: refund.status,
      statusType: typeof refund.status,
      statusLower: refund.status ? refund.status.toString().toLowerCase() : 'no status',
      willBeIncluded: !refund.status || (refund.status && ['completed', 'success'].includes(refund.status.toString().toLowerCase())),
      fullObject: refund
    });
    
    // Log all properties of the refund object
    console.log(`Refund ${index + 1} - All properties:`, Object.keys(refund));
    console.log(`Refund ${index + 1} - Full object:`, JSON.stringify(refund, null, 2));
  });

  // For now, let's include ALL refunds to test if the calculation works
  console.log('=== TESTING: Including ALL refunds ===');
  const totalRefundsAll = refunds.reduce((sum, r) => {
    const amount = parseFloat(r.amount);
    console.log(`Including refund: ${r.id}, amount: ${amount}`);
    return sum + (isNaN(amount) ? 0 : amount);
  }, 0);
  console.log('Total if all refunds included:', totalRefundsAll);

  // TEMPORARY: Include ALL refunds for testing
  console.log('=== TEMPORARY: Testing with ALL refunds included ===');
  const totalRefundsTest = refunds.reduce((sum, r) => {
    const amount = parseFloat(r.amount);
    console.log(`Test - Adding refund: ${r.id}, amount: ${amount}, isValid: ${!isNaN(amount)}`);
    return sum + (isNaN(amount) ? 0 : amount);
  }, 0);
  
  // Override the calculated total with the test total for now
  const totalRefunds = totalRefundsTest;

  // Debug logging
  console.log('PaymentHistory calculations:', {
    payments: payments,
    refunds: refunds,
    totalPaid,
    totalRefunds,
    totalRefundsAll, // Add this for comparison
    filteredPayments: payments.filter(p => p.status === 'SUCCESS'),
    filteredRefunds: refunds.filter(r => {
      if (!r.status) return true;
      const status = r.status.toString().toLowerCase();
      return status === 'completed' || status === 'success';
    }),
    refundStatuses: refunds.map(r => ({ id: r.id, status: r.status, amount: r.amount, type: typeof r.amount }))
  });

  const netPaidAmount = totalPaid - totalRefunds;
  const remainingAmount = (booking.finalAmount || booking.totalPrice || 0) - netPaidAmount;

  if (loading) {
    return (
      <>
        {/* CSS for spinner animation */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
        <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}><HourglassIcon size="1em" aria-hidden="true" /></div>
          Loading payment history...
        </div>
      </>
    );
  }

  return (
    <>
      {/* CSS for spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div>
      {/* Payment Summary */}
      <div style={{
        backgroundColor: '#F5F3EB',
        padding: '16px',
        borderRadius: '8px',
        border: '1px solid #E3DFD3',
        marginBottom: '20px'
      }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
          Payment Summary
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', fontSize: '14px' }}>
          <div>
            <div style={{ fontWeight: '600', color: '#24332A' }}>Total Amount</div>
            <div style={{ color: '#5F6B61' }}>₹{(booking.finalAmount || booking.totalPrice || 0).toFixed(2)}</div>
          </div>
          <div>
            <div style={{ fontWeight: '600', color: '#24332A' }}>Total Paid</div>
            <div style={{ color: '#163626' }}>₹{totalPaid.toFixed(2)}</div>
          </div>
          <div>
            <div style={{ fontWeight: '600', color: '#24332A' }}>Total Refunds</div>
            <div style={{ color: '#8E3628' }}>₹{totalRefunds.toFixed(2)}</div>
          </div>
          <div>
            <div style={{ fontWeight: '600', color: '#24332A' }}>Net Paid</div>
            <div style={{ color: netPaidAmount >= 0 ? '#163626' : '#8E3628' }}>₹{netPaidAmount.toFixed(2)}</div>
          </div>
          <div>
            <div style={{ fontWeight: '600', color: '#24332A' }}>Remaining</div>
            <div style={{ color: remainingAmount > 0 ? '#8E3628' : '#163626' }}>
              ₹{remainingAmount.toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontWeight: '600', color: '#24332A' }}>Status</div>
            <div style={{ 
              color: remainingAmount <= 0 ? '#163626' : '#8E3628',
              fontWeight: '600'
            }}>
              {remainingAmount <= 0 ? 'Paid' : 'Pending'}
            </div>
          </div>
        </div>
      </div>

      {/* Payment and Refund History */}
      <div>
        <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
          Payment & Refund History ({payments.length + refunds.length})
        </h4>
        
        {payments.length === 0 && refunds.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}><CreditCardIcon size="1em" aria-hidden="true" /></div>
            <p>No payments or refunds recorded yet</p>
            <p style={{ fontSize: '14px' }}>
              Use the "Add Payment" or "Add Refund" buttons to record transactions for this booking.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Combine and sort payments and refunds by date */}
            {[
              ...payments.map(payment => ({ ...payment, type: 'payment' })),
              ...refunds.map(refund => ({ ...refund, type: 'refund' }))
            ]
              .sort((a, b) => new Date(b.paymentDate || b.refundDate || b.createdAt) - new Date(a.paymentDate || a.refundDate || a.createdAt))
              .map((transaction) => {
                const isPayment = transaction.type === 'payment';
                const isRefund = transaction.type === 'refund';
                const statusColors = isPayment ? getPaymentStatusColor(transaction.status) : { bg: '#E5B3A9', text: '#5F241B', border: '#D48A7C' };
                const isEditingPayment = editingPayment === transaction.id && isPayment;
                const isEditingRefund = editingRefund === transaction.id && isRefund;
                const isEditing = isEditingPayment || isEditingRefund;
                
                return (
                  <div
                    key={`${transaction.type}-${transaction.id}`}
                    style={{
                      border: isEditing ? '2px solid #1A3E2A' : isRefund ? '2px solid #8E3628' : '1px solid #E3DFD3',
                      borderRadius: '8px',
                      padding: '16px',
                      backgroundColor: isEditing ? '#F5F3EB' : isRefund ? '#F9EEEB' : 'white',
                    }}
                  >
                    {isEditingPayment ? (
                      // Edit Mode for Payment
                      <>
                        <div style={{ marginBottom: '16px' }}>
                          <h5 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#24332A' }}>
                            Edit Payment
                          </h5>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                          {/* Amount */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#36433B' }}>
                              Amount *
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={editForm.amount}
                              onChange={(e) => setEditForm(prev => ({ ...prev, amount: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #D2CDBF',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            />
                          </div>

                          {/* Status */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#36433B' }}>
                              Status *
                            </label>
                            <select
                              value={editForm.status}
                              onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #D2CDBF',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            >
                              <option value="PENDING">Pending</option>
                              <option value="SUCCESS">Success</option>
                              <option value="FAILED">Failed</option>
                              <option value="REFUNDED">Refunded</option>
                            </select>
                          </div>

                          {/* Method */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#36433B' }}>
                              Payment Method
                            </label>
                            <select
                              value={editForm.method}
                              onChange={(e) => setEditForm(prev => ({ ...prev, method: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #D2CDBF',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            >
                              <option value="cash">Cash</option>
                              <option value="upi">UPI</option>
                              <option value="credit_card">Credit Card</option>
                              <option value="debit_card">Debit Card</option>
                              <option value="netbanking">Net Banking</option>
                              <option value="stripe">Stripe</option>
                              <option value="razorpay">Razorpay</option>
                            </select>
                          </div>

                          {/* Transaction ID */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#36433B' }}>
                              Transaction ID
                            </label>
                            <input
                              type="text"
                              value={editForm.transactionId}
                              onChange={(e) => setEditForm(prev => ({ ...prev, transactionId: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #D2CDBF',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                              placeholder="Optional transaction ID"
                            />
                          </div>
                          
                          {/* Payment Date */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#36433B' }}>
                              Payment Date
                            </label>
                            <input
                              type="date"
                              value={editForm.paymentDate}
                              onChange={(e) => setEditForm(prev => ({ ...prev, paymentDate: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #D2CDBF',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                              placeholder="Payment date"
                            />
                          </div>
                        </div>

                        {/* Reason */}
                        <div style={{ marginTop: '12px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#36433B' }}>
                            Reason for Update *
                          </label>
                          <textarea
                            value={editForm.reason}
                            onChange={(e) => setEditForm(prev => ({ ...prev, reason: e.target.value }))}
                            placeholder="e.g., Correcting payment amount due to calculation error"
                            style={{
                              width: '100%',
                              minHeight: '60px',
                              padding: '8px 12px',
                              border: '1px solid #D2CDBF',
                              borderRadius: '6px',
                              fontSize: '14px',
                              resize: 'vertical'
                            }}
                          />
                        </div>

                        {/* Admin Note */}
                        <div style={{ marginTop: '12px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#36433B' }}>
                            Admin Note (Optional)
                          </label>
                          <textarea
                            value={editForm.adminNote}
                            onChange={(e) => setEditForm(prev => ({ ...prev, adminNote: e.target.value }))}
                            placeholder="Additional notes about this payment update..."
                            style={{
                              width: '100%',
                              minHeight: '50px',
                              padding: '8px 12px',
                              border: '1px solid #D2CDBF',
                              borderRadius: '6px',
                              fontSize: '14px',
                              resize: 'vertical'
                            }}
                          />
                        </div>

                        {/* Edit Buttons */}
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            disabled={updatingPayment}
                            style={{
                              padding: '8px 16px',
                              border: '1px solid #D2CDBF',
                              borderRadius: '6px',
                              backgroundColor: 'white',
                              color: '#36433B',
                              fontSize: '12px',
                              fontWeight: '500',
                              cursor: updatingPayment ? 'not-allowed' : 'pointer',
                              opacity: updatingPayment ? 0.7 : 1
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleUpdatePayment}
                            disabled={updatingPayment || !editForm.reason.trim() || !editForm.amount}
                            style={{
                              padding: '8px 16px',
                              border: 'none',
                              borderRadius: '6px',
                              backgroundColor: updatingPayment || !editForm.reason.trim() || !editForm.amount ? '#A5A89B' : '#1A3E2A',
                              color: 'white',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: updatingPayment || !editForm.reason.trim() || !editForm.amount ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            {updatingPayment ? (
                              <>
                                <div style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                                Updating...
                              </>
                            ) : (
                              'Update Payment'
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  ) : isEditingRefund ? (
                    // Edit Mode for Refund
                    <>
                      <div style={{ marginBottom: '16px' }}>
                        <h5 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#8E3628' }}>
                          Edit Refund
                        </h5>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                          {/* Amount */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#36433B' }}>
                              Amount *
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={editRefundForm.amount}
                              onChange={(e) => setEditRefundForm(prev => ({ ...prev, amount: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #D2CDBF',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            />
                          </div>

                          {/* Status */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#36433B' }}>
                              Status *
                            </label>
                            <select
                              value={editRefundForm.status}
                              onChange={(e) => setEditRefundForm(prev => ({ ...prev, status: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #D2CDBF',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            >
                              <option value="PENDING">Pending</option>
                              <option value="PROCESSING">Processing</option>
                              <option value="SUCCESS">Success</option>
                              <option value="COMPLETED">Completed</option>
                              <option value="FAILED">Failed</option>
                              <option value="CANCELLED">Cancelled</option>
                            </select>
                          </div>

                          {/* Method */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#36433B' }}>
                              Refund Method
                            </label>
                            <select
                              value={editRefundForm.method}
                              onChange={(e) => setEditRefundForm(prev => ({ ...prev, method: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #D2CDBF',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            >
                              <option value="cash">Cash</option>
                              <option value="upi">UPI</option>
                              <option value="credit_card">Credit Card</option>
                              <option value="debit_card">Debit Card</option>
                              <option value="netbanking">Net Banking</option>
                              <option value="stripe">Stripe</option>
                              <option value="razorpay">Razorpay</option>
                            </select>
                          </div>

                          {/* Transaction ID */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#36433B' }}>
                              Transaction ID
                            </label>
                            <input
                              type="text"
                              value={editRefundForm.transactionId}
                              onChange={(e) => setEditRefundForm(prev => ({ ...prev, transactionId: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #D2CDBF',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                              placeholder="Optional transaction ID"
                            />
                          </div>
                        </div>

                        {/* Reason */}
                        <div style={{ marginTop: '12px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#36433B' }}>
                            Refund Reason *
                          </label>
                          <textarea
                            value={editRefundForm.reason}
                            onChange={(e) => setEditRefundForm(prev => ({ ...prev, reason: e.target.value }))}
                            placeholder="e.g., Customer cancellation, Service not provided"
                            style={{
                              width: '100%',
                              minHeight: '60px',
                              padding: '8px 12px',
                              border: '1px solid #D2CDBF',
                              borderRadius: '6px',
                              fontSize: '14px',
                              resize: 'vertical'
                            }}
                          />
                        </div>

                        {/* Admin Note */}
                        <div style={{ marginTop: '12px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#36433B' }}>
                            Admin Note (Optional)
                          </label>
                          <textarea
                            value={editRefundForm.adminNote}
                            onChange={(e) => setEditRefundForm(prev => ({ ...prev, adminNote: e.target.value }))}
                            placeholder="Additional notes about this refund update..."
                            style={{
                              width: '100%',
                              minHeight: '50px',
                              padding: '8px 12px',
                              border: '1px solid #D2CDBF',
                              borderRadius: '6px',
                              fontSize: '14px',
                              resize: 'vertical'
                            }}
                          />
                        </div>

                        {/* Edit Buttons */}
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
                          <button
                            type="button"
                            onClick={handleCancelRefundEdit}
                            disabled={updatingRefund}
                            style={{
                              padding: '8px 16px',
                              border: '1px solid #D2CDBF',
                              borderRadius: '6px',
                              backgroundColor: 'white',
                              color: '#36433B',
                              fontSize: '12px',
                              fontWeight: '500',
                              cursor: updatingRefund ? 'not-allowed' : 'pointer',
                              opacity: updatingRefund ? 0.7 : 1
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleUpdateRefund}
                            disabled={updatingRefund || !editRefundForm.reason.trim() || !editRefundForm.amount}
                            style={{
                              padding: '8px 16px',
                              border: 'none',
                              borderRadius: '6px',
                              backgroundColor: updatingRefund || !editRefundForm.reason.trim() || !editRefundForm.amount ? '#A5A89B' : '#8E3628',
                              color: 'white',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: updatingRefund || !editRefundForm.reason.trim() || !editRefundForm.amount ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            {updatingRefund ? (
                              <>
                                <div style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                                Updating...
                              </>
                            ) : (
                              'Update Refund'
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    // View Mode
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: isRefund ? '#8E3628' : '#24332A' }}>
                              {isRefund ? '-' : ''}₹{transaction.amount}
                            </span>
                            <span style={{
                              fontSize: '12px',
                              fontWeight: '600',
                              color: statusColors.text,
                              backgroundColor: statusColors.bg,
                              border: `1px solid ${statusColors.border}`,
                              padding: '2px 8px',
                              borderRadius: '12px',
                            }}>
                              {isRefund ? 'REFUND' : transaction.status}
                            </span>
                            {isRefund && (
                              <span style={{
                                fontSize: '11px',
                                fontWeight: '500',
                                color: '#8E3628',
                                backgroundColor: '#F9EEEB',
                                border: '1px solid #E5B3A9',
                                padding: '1px 6px',
                                borderRadius: '8px',
                              }}>
                                {transaction.reason || 'Refund'}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '14px', color: isRefund ? '#8E3628' : '#5F6B61' }}>
                            {isRefund ? (
                              <>
                                {getMethodDisplay(transaction.method || transaction.refundMethod)} • {formatDate(transaction.refundDate || transaction.createdAt)}
                                {transaction.description && (
                                  <div style={{ fontSize: '12px', color: '#5F241B', marginTop: '2px' }}>
                                    {transaction.description}
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                {getMethodDisplay(transaction.method)} • {formatDate(transaction.paymentDate || transaction.createdAt)}
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {/* Payment Action Buttons */}
                          {isPayment && (
                            <>
                              {/* Download Invoice Button */}
                              <button
                                type="button"
                                onClick={() => handleDownloadPaymentInvoice(transaction)}
                                style={{
                                  padding: '6px 12px',
                                  border: '1px solid #163626',
                                  borderRadius: '6px',
                                  backgroundColor: 'white',
                                  color: '#163626',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                }}
                              >
                                Invoice
                              </button>
                              
                              {/* Refund Button */}
                              {transaction.status === 'SUCCESS' && onRefund && (
                                <button
                                  type="button"
                                  onClick={() => handleRefund(transaction)}
                                  style={{
                                    padding: '6px 12px',
                                    border: '1px solid #8E3628',
                                    borderRadius: '6px',
                                    backgroundColor: 'white',
                                    color: '#8E3628',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                  }}
                                >
                                  Refund
                                </button>
                              )}
                              
                              {/* Edit Button (Admin only) */}
                              {isAdmin && (
                                <button
                                  type="button"
                                  onClick={() => handleEditPayment(transaction)}
                                  style={{
                                    padding: '6px 12px',
                                    border: '1px solid #1A3E2A',
                                    borderRadius: '6px',
                                    backgroundColor: 'white',
                                    color: '#1A3E2A',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                  }}
                                >
                                  Edit
                                </button>
                              )}
                              
                              {/* Delete Button (Admin only) */}
                              {isAdmin && (
                                <button
                                  type="button"
                                  onClick={() => handleShowDeleteConfirm(transaction)}
                                  style={{
                                    padding: '6px 12px',
                                    border: '1px solid #8E3628',
                                    borderRadius: '6px',
                                    backgroundColor: 'white',
                                    color: '#8E3628',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                  }}
                                >
                                  Delete
                                </button>
                              )}
                            </>
                          )}
                          
                          {/* Refund Action Buttons (Admin only) */}
                          {isRefund && isAdmin && (
                            <>
                              {/* Edit Refund Button */}
                              <button
                                type="button"
                                onClick={() => handleEditRefund(transaction)}
                                style={{
                                  padding: '6px 12px',
                                  border: '1px solid #1A3E2A',
                                  borderRadius: '6px',
                                  backgroundColor: 'white',
                                  color: '#1A3E2A',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                }}
                              >
                                Edit
                              </button>
                              
                              {/* Delete Refund Button */}
                              <button
                                type="button"
                                onClick={() => handleShowDeleteRefundConfirm(transaction)}
                                style={{
                                  padding: '6px 12px',
                                  border: '1px solid #8E3628',
                                  borderRadius: '6px',
                                  backgroundColor: 'white',
                                  color: '#8E3628',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                }}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Tax Breakdown if available (only for payments) */}
                      {isPayment && transaction.totalTaxAmount && transaction.totalTaxAmount > 0 && (
                        <div style={{
                          backgroundColor: '#F5F3EB',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          color: '#5F6B61',
                          marginBottom: '8px'
                        }}>
                          <div><strong>Base Amount:</strong> ₹{transaction.baseAmount || 0}</div>
                          <div><strong>Tax Amount:</strong> ₹{transaction.totalTaxAmount}</div>
                          {transaction.sgstAmount > 0 && <div>SGST ({transaction.sgstRate}%): ₹{transaction.sgstAmount}</div>}
                          {transaction.cgstAmount > 0 && <div>CGST ({transaction.cgstRate}%): ₹{transaction.cgstAmount}</div>}
                          {transaction.igstAmount > 0 && <div>IGST ({transaction.igstRate}%): ₹{transaction.igstAmount}</div>}
                        </div>
                      )}
                      
                      {/* Transaction ID */}
                      <div style={{ fontSize: '12px', color: '#A5A89B' }}>
                        {isRefund ? 'Refund' : 'Payment'} ID: {transaction.id.slice(-8)}
                        {transaction.transactionId && ` • Transaction: ${transaction.transactionId.slice(-8)}`}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Hidden Payment Invoice Template for PDF generation */}
      {invoicePayment && (
        <div style={{ position: 'fixed', left: '-9999px', top: 0 }}>
          <div ref={paymentInvoiceRef} style={{
            width: '600px',
            padding: '40px',
            backgroundColor: '#ffffff',
            fontFamily: 'Arial, Helvetica, sans-serif',
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '28px', borderBottom: '2px solid #1A3E2A', paddingBottom: '16px' }}>
              <h2 style={{ margin: '0 0 4px 0', fontSize: '22px', fontWeight: '700', color: '#1A3E2A' }}>Payment Invoice</h2>
              <p style={{ margin: 0, fontSize: '13px', color: '#5F6B61' }}>Booking: {booking?.bookingNumber || 'N/A'}</p>
            </div>

            {/* Guest Details */}
            <div style={{ marginBottom: '20px', padding: '14px', backgroundColor: '#F5F3EB', borderRadius: '8px', border: '1px solid #E3DFD3' }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '700', color: '#36433B' }}>Guest Details</h4>
              <table style={{ width: '100%', fontSize: '13px', color: '#4A564D', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr><td style={{ padding: '4px 0', fontWeight: '600', width: '140px' }}>Name</td><td style={{ padding: '4px 0' }}>{booking?.user?.name || booking?.guestName || 'N/A'}</td></tr>
                  <tr><td style={{ padding: '4px 0', fontWeight: '600' }}>Email</td><td style={{ padding: '4px 0' }}>{booking?.user?.email || booking?.guestEmail || 'N/A'}</td></tr>
                  <tr><td style={{ padding: '4px 0', fontWeight: '600' }}>Phone</td><td style={{ padding: '4px 0' }}>{booking?.user?.phone || booking?.guestPhone || 'N/A'}</td></tr>
                </tbody>
              </table>
            </div>

            {/* Payment Details */}
            <div style={{ marginBottom: '20px', padding: '14px', backgroundColor: '#F5F3EB', borderRadius: '8px', border: '1px solid #E3DFD3' }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '700', color: '#36433B' }}>Payment Details</h4>
              <table style={{ width: '100%', fontSize: '13px', color: '#4A564D', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr><td style={{ padding: '4px 0', fontWeight: '600', width: '140px' }}>Payment ID</td><td style={{ padding: '4px 0' }}>{invoicePayment.id}</td></tr>
                  {invoicePayment.transactionId && <tr><td style={{ padding: '4px 0', fontWeight: '600' }}>Transaction ID</td><td style={{ padding: '4px 0' }}>{invoicePayment.transactionId}</td></tr>}
                  <tr><td style={{ padding: '4px 0', fontWeight: '600' }}>Status</td><td style={{ padding: '4px 0' }}>{invoicePayment.status}</td></tr>
                  <tr><td style={{ padding: '4px 0', fontWeight: '600' }}>Payment Method</td><td style={{ padding: '4px 0' }}>{(getMethodDisplay(invoicePayment.method) || invoicePayment.method || 'N/A').replace(/[^\w\s]/g, '').trim()}</td></tr>
                  <tr><td style={{ padding: '4px 0', fontWeight: '600' }}>Payment Date</td><td style={{ padding: '4px 0' }}>{formatDateIST(invoicePayment.paymentDate || invoicePayment.createdAt)}</td></tr>
                </tbody>
              </table>
            </div>

            {/* Amount Section */}
            <div style={{ marginBottom: '20px', padding: '14px', backgroundColor: '#F0F3EE', borderRadius: '8px', border: '2px solid #1A3E2A' }}>
              <table style={{ width: '100%', fontSize: '13px', color: '#4A564D', borderCollapse: 'collapse' }}>
                <tbody>
                  {invoicePayment.baseAmount != null && (
                    <tr><td style={{ padding: '4px 0', fontWeight: '600', width: '140px' }}>Base Amount</td><td style={{ padding: '4px 0' }}>₹{Number(invoicePayment.baseAmount).toFixed(2)}</td></tr>
                  )}
                  {invoicePayment.sgstAmount > 0 && (
                    <tr><td style={{ padding: '4px 0', fontWeight: '600' }}>SGST ({invoicePayment.sgstRate}%)</td><td style={{ padding: '4px 0' }}>₹{Number(invoicePayment.sgstAmount).toFixed(2)}</td></tr>
                  )}
                  {invoicePayment.cgstAmount > 0 && (
                    <tr><td style={{ padding: '4px 0', fontWeight: '600' }}>CGST ({invoicePayment.cgstRate}%)</td><td style={{ padding: '4px 0' }}>₹{Number(invoicePayment.cgstAmount).toFixed(2)}</td></tr>
                  )}
                  {invoicePayment.igstAmount > 0 && (
                    <tr><td style={{ padding: '4px 0', fontWeight: '600' }}>IGST ({invoicePayment.igstRate}%)</td><td style={{ padding: '4px 0' }}>₹{Number(invoicePayment.igstAmount).toFixed(2)}</td></tr>
                  )}
                  {invoicePayment.totalTaxAmount > 0 && (
                    <tr><td style={{ padding: '4px 0', fontWeight: '600' }}>Total Tax</td><td style={{ padding: '4px 0' }}>₹{Number(invoicePayment.totalTaxAmount).toFixed(2)}</td></tr>
                  )}
                  <tr style={{ borderTop: '2px solid #1A3E2A' }}>
                    <td style={{ padding: '8px 0 4px', fontWeight: '700', fontSize: '16px', color: '#1A3E2A' }}>Total Amount</td>
                    <td style={{ padding: '8px 0 4px', fontWeight: '700', fontSize: '16px', color: '#1A3E2A' }}>₹{Number(invoicePayment.amount).toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div style={{ textAlign: 'center', fontSize: '11px', color: '#A5A89B', borderTop: '1px solid #E3DFD3', paddingTop: '12px' }}>
              <p style={{ margin: '0 0 2px 0' }}>Invoice downloaded on: {formatDateIST(new Date().toISOString())}</p>
              <p style={{ margin: 0 }}>This is a computer-generated invoice.</p>
            </div>
          </div>
        </div>
      )}

      {/* Delete Payment Confirmation Modal */}
      {showDeleteConfirm && deletingPayment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#E9DFCE',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}><Trash2Icon size="1em" aria-hidden="true" /></div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600', color: '#8E3628' }}>
                Delete Payment Record
              </h3>
              <p style={{ margin: '0', fontSize: '14px', color: '#5F6B61' }}>
                You are about to permanently delete this payment record:
              </p>
            </div>

            {/* Payment Details */}
            <div style={{
              backgroundColor: '#F9EEEB',
              border: '1px solid #E5B3A9',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#5F241B', marginBottom: '8px' }}>
                ₹{deletingPayment.amount}
              </div>
              <div style={{ fontSize: '14px', color: '#481B14' }}>
                {getMethodDisplay(deletingPayment.method)} • {formatDate(deletingPayment.paymentDate || deletingPayment.createdAt)}
              </div>
              <div style={{ fontSize: '12px', color: '#5F241B', marginTop: '4px' }}>
                Payment ID: {deletingPayment.id.slice(-8)}
              </div>
            </div>

            {/* Warning */}
            <div style={{
              backgroundColor: '#F3E6CB',
              border: '1px solid #C9903C',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '16px'
            }}>
              <p style={{ margin: '0', fontSize: '13px', color: '#684A1F', fontWeight: '600' }}>
                This action cannot be undone. The payment record will be permanently deleted from the system.
              </p>
            </div>

            {/* Reason Field */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                Reason for Deletion *
              </label>
              <textarea
                value={deleteForm.reason}
                onChange={(e) => setDeleteForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="e.g., Duplicate payment entry created by system error"
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '10px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Admin Note Field */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                Admin Note (Optional)
              </label>
              <textarea
                value={deleteForm.adminNote}
                onChange={(e) => setDeleteForm(prev => ({ ...prev, adminNote: e.target.value }))}
                placeholder="Additional notes about this deletion..."
                style={{
                  width: '100%',
                  minHeight: '60px',
                  padding: '10px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleCancelDelete}
                disabled={updatingPayment}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#36433B',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: updatingPayment ? 'not-allowed' : 'pointer',
                  opacity: updatingPayment ? 0.7 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeletePayment}
                disabled={updatingPayment || !deleteForm.reason.trim()}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: updatingPayment || !deleteForm.reason.trim() ? '#A5A89B' : '#8E3628',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: updatingPayment || !deleteForm.reason.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {updatingPayment ? (
                  <>
                    <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                    Deleting...
                  </>
                ) : (
                  'Delete Payment'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Refund Confirmation Modal */}
      {showDeleteRefundConfirm && deletingRefund && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#E9DFCE',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}><Trash2Icon size="1em" aria-hidden="true" /></div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600', color: '#8E3628' }}>
                Delete Refund Record
              </h3>
              <p style={{ margin: '0', fontSize: '14px', color: '#5F6B61' }}>
                You are about to permanently delete this refund record:
              </p>
            </div>

            {/* Refund Details */}
            <div style={{
              backgroundColor: '#F9EEEB',
              border: '1px solid #E5B3A9',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#5F241B', marginBottom: '8px' }}>
                -₹{deletingRefund.amount}
              </div>
              <div style={{ fontSize: '14px', color: '#481B14' }}>
                {getMethodDisplay(deletingRefund.method || deletingRefund.refundMethod)} • {formatDate(deletingRefund.refundDate || deletingRefund.createdAt)}
              </div>
              <div style={{ fontSize: '12px', color: '#5F241B', marginTop: '4px' }}>
                Refund ID: {deletingRefund.id.slice(-8)}
              </div>
              {deletingRefund.reason && (
                <div style={{ fontSize: '12px', color: '#5F241B', marginTop: '4px', fontStyle: 'italic' }}>
                  Reason: {deletingRefund.reason}
                </div>
              )}
            </div>

            {/* Warning */}
            <div style={{
              backgroundColor: '#F3E6CB',
              border: '1px solid #C9903C',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '16px'
            }}>
              <p style={{ margin: '0', fontSize: '13px', color: '#684A1F', fontWeight: '600' }}>
                This action cannot be undone. The refund record will be permanently deleted from the system.
              </p>
            </div>

            {/* Reason Field */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                Reason for Deletion *
              </label>
              <textarea
                value={deleteRefundForm.reason}
                onChange={(e) => setDeleteRefundForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="e.g., Duplicate refund entry created by system error"
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '10px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Admin Note Field */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                Admin Note (Optional)
              </label>
              <textarea
                value={deleteRefundForm.adminNote}
                onChange={(e) => setDeleteRefundForm(prev => ({ ...prev, adminNote: e.target.value }))}
                placeholder="Additional notes about this deletion..."
                style={{
                  width: '100%',
                  minHeight: '60px',
                  padding: '10px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleCancelDeleteRefund}
                disabled={updatingRefund}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#36433B',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: updatingRefund ? 'not-allowed' : 'pointer',
                  opacity: updatingRefund ? 0.7 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteRefund}
                disabled={updatingRefund || !deleteRefundForm.reason.trim()}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: updatingRefund || !deleteRefundForm.reason.trim() ? '#A5A89B' : '#8E3628',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: updatingRefund || !deleteRefundForm.reason.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {updatingRefund ? (
                  <>
                    <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                    Deleting...
                  </>
                ) : (
                  'Delete Refund'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </>
  );
};

export default PaymentHistory;
